import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '../stores/user-store'
import { isTokenExpired, clearAuthData } from '../utils/tokenValidator'
import { useToast } from 'vuestic-ui'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        path: '/admin/personal-reports',
        name: 'personal-reports',
        component: () => import('@/pages/admin/pages/PersonalReportList.vue'),
        meta: { requiresAuth: true, title: '个人客户报备信息' },
      },
      {
        path: '/admin/company-reports',
        name: 'company-reports',
        component: () => import('@/pages/admin/pages/CompanyReportList.vue'),
        meta: { requiresAuth: true, title: '企业客户报备信息' },
      },
      {
        path: '/admin/my-personal-reports',
        name: 'my-personal-reports',
        component: () => import('@/pages/admin/pages/MyPersonalReportList.vue'),
        meta: { requiresAuth: true, title: '我的个人用户报备' },
      },
      {
        path: '/admin/my-company-reports',
        name: 'my-company-reports',
        component: () => import('@/pages/admin/pages/MyCompanyReportList.vue'),
        meta: { requiresAuth: true, title: '我的企业用户报备' },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// 全局前置守卫：处理认证和重定向
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  const token = localStorage.getItem('token')

  // 检查token是否过期
  if (token && isTokenExpired(token)) {
    // Token已过期，清除认证信息
    clearAuthData()

    // 如果不是去登录页，显示提示并重定向
    if (to.name !== 'login') {
      const { init: notify } = useToast()
      notify({
        message: '您的登录已过期，请重新登录',
        color: 'warning',
        duration: 3000,
        position: 'top-right',
      })

      next({ name: 'login' })
      return
    }
  }

  // 如果要去的是登录页面且已经登录，重定向到首页
  if (to.name === 'login' && userStore.isLoggedIn) {
    next({ name: 'dashboard' })
    return
  }

  // 如果需要认证且未登录，重定向到登录页
  if (to.matched.some((record) => record.meta.requiresAuth) && !userStore.isLoggedIn) {
    next({ name: 'login' })
    return
  }

  next()
})

export default router
